/**
 * File · Edit · View · Insert · Format · Tools · Help.
 *
 * THE MENUS ARE WHERE PEOPLE LOOK, which is the whole argument for them. A toolbar carries the
 * dozen things done constantly; everything else — import a Word file, count the words, find and
 * replace, print — has no home without a menu bar, and somebody who has used a word processor
 * looks under File before they look anywhere else.
 *
 * INSERT IS HERE THOUGH IT WAS NOT ASKED FOR. Ed named six menus; Google has seven, and the
 * seventh is where tables, links and rules live. Folding those into Format would put "insert a
 * three-column table" under a heading that means "change what is already there".
 *
 * NOTHING IS LISTED THAT DOES NOT WORK. A menu of greyed-out promises is worse than a short menu:
 * it teaches people the product is unfinished everywhere rather than in the one place it is. Where
 * something genuinely belongs in the vocabulary but is not built — Download as PDF, version
 * history, suggesting mode — it is listed, disabled, and says WHY in its tooltip, because its
 * absence from the menu would be read as "this product cannot do that".
 */
import React from "react";
import {
    Box, Divider, ListItemText, Menu, MenuItem, Tooltip, Typography,
} from "@mui/material";

import Symbol from "./Symbol";
import { CHROME_FONT, CHROME_INK } from "./EditorToolbar";
import { Grey3, Grey5, Grey17 } from "../../theme/styles";

export type MenuAction = {
    label: string;
    /**
     * A Material Symbols name for the left column.
     *
     * EVERY ROW IN GOOGLE'S MENUS CARRIES ONE, and the column is what makes a long menu scannable:
     * the eye finds "the one with the printer" far faster than it reads fourteen labels. A row with
     * no icon still reserves the column, so nothing shuffles sideways.
     */
    icon?: string;
    /** Shown right-aligned, as a word processor shows its shortcuts. */
    shortcut?: string;
    onClick?: () => void;
    /** Present = disabled, and this is the sentence saying why. */
    unavailable?: string;
    divider?: boolean;
    checked?: boolean;
    /** Children, drawn with the ▸ a word processor uses to promise them. */
    submenu?: MenuAction[];
};

export type MenuDefinition = { name: string; items: MenuAction[] };


/**
 * One line of a menu: tick, icon, label, shortcut or submenu arrow.
 *
 * THE COLUMNS ARE FIXED WIDTH whether or not a row uses them, so a menu does not shuffle sideways
 * when one item is ticked and its neighbour is not.
 */
function Row({ item, onDone }: { item: MenuAction; onDone: () => void }) {
    const [sub, setSub] = React.useState<HTMLElement | null>(null);
    const disabled = !!item.unavailable || (!item.onClick && !item.submenu);

    const body = (
        <MenuItem disabled={disabled}
                  onClick={(e) => {
                      if (item.submenu) { setSub(e.currentTarget); return; }
                      onDone();
                      // THE COMMAND RUNS AFTER THE MENU HAS GONE, and that is the whole reason
                      // typing after a menu command went nowhere. A MUI Menu is a Modal with a
                      // focus trap: while it is open it pulls the keyboard back to itself, so a
                      // command that ran synchronously here -- every one of them ends with
                      // editor.focus() -- had its focus reverted the instant it took it. onDone()
                      // only SCHEDULES the close; React had not unmounted the modal yet.
                      //
                      // Two frames: one for React to commit the close, one for the browser to
                      // settle focus after the trap releases it.
                      const run = item.onClick;
                      if (run) requestAnimationFrame(() => requestAnimationFrame(run));
                  }}
                  sx={{ fontSize: 14, py: 0.55, fontFamily: CHROME_FONT }}>
            <Box sx={{ width: 14, mr: 0.5, fontSize: 13, color: Grey17, flexShrink: 0 }}>
                {item.checked ? "✓" : ""}
            </Box>
            <Box sx={{ width: 24, flexShrink: 0, display: "flex", alignItems: "center" }}>
                {item.icon ? <Symbol name={item.icon} size={18} colour={disabled ? "#BDC1C6" : CHROME_INK}/> : null}
            </Box>
            <ListItemText slotProps={{ primary: { style: { fontSize: 14, fontFamily: CHROME_FONT } } }}>
                {item.label}
            </ListItemText>
            {item.shortcut && (
                <Typography sx={{ fontSize: 12, color: Grey5, ml: 3, fontFamily: CHROME_FONT }}>
                    {item.shortcut}
                </Typography>
            )}
            {item.submenu && <Symbol name="arrow_right" size={18} sx={{ ml: 2, color: Grey5 }}/>}
        </MenuItem>
    );

    return (
        <>
            {item.unavailable
                /* A disabled MenuItem swallows hover, so the tooltip needs its own element --
                   otherwise the explanation for why something is greyed out is unreachable. */
                ? <Tooltip title={item.unavailable} placement="right"><span>{body}</span></Tooltip>
                : body}
            {item.submenu && (
                <Menu anchorEl={sub} open={!!sub} onClose={() => setSub(null)}
                      disableRestoreFocus
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      slotProps={{ list: { dense: true,
                                           sx: { minWidth: 268, py: 0.5, fontFamily: CHROME_FONT } } }}>
                    {/* DIVIDERS IN HERE TOO, which they were not. The top-level menu drew them and
                        a submenu dropped them silently, so Format ▸ Table came out as nineteen
                        undifferentiated rows -- the right items in the right order, reading as a
                        list rather than as the five groups it is. A submenu is a menu. */}
                    {item.submenu.map((child, index) => ([
                        child.divider && index > 0
                            ? <Divider key={`d${index}`} sx={{ my: 0.5 }}/> : null,
                        <Row key={child.label} item={child}
                             onDone={() => { setSub(null); onDone(); }}/>,
                    ]))}
                </Menu>
            )}
        </>
    );
}

export default function EditorMenuBar({ menus }: { menus: MenuDefinition[] }) {
    const [open, setOpen] = React.useState<{ name: string; el: HTMLElement } | null>(null);

    // ONCE ONE MENU IS OPEN, HOVERING OPENS THE NEXT -- which is how every menu bar since 1984 has
    // behaved, and its absence is felt immediately as stickiness rather than noticed as a feature.
    const hover = (name: string) => (event: React.MouseEvent<HTMLElement>) => {
        if (open && open.name !== name) setOpen({ name, el: event.currentTarget });
    };

    const items = menus.find(one => one.name === open?.name)?.items ?? [];

    // THE GAP IS ~24px BETWEEN WORDS -- 10px of padding either side of each item plus 4px between
    // them. It was 18px, which reads tighter than Google Docs at the same font size: the bar looked
    // like one long string rather than seven things to choose from.
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", px: 0.5, mb: 0.5 }}>
            {menus.map(menu => (
                <Typography key={menu.name} component="button" type="button"
                            onClick={(e) => setOpen({ name: menu.name, el: e.currentTarget })}
                            onMouseEnter={hover(menu.name)}
                            aria-haspopup="true"
                            aria-expanded={open?.name === menu.name}
                            sx={{ fontSize: 14, color: "#444746", px: "10px", py: 0.4, border: "none",
                                  background: open?.name === menu.name ? "#E8F0FE" : "none",
                                  borderRadius: 1, cursor: "pointer",
                                  // Google Docs' own menu-bar metrics: Roboto 14 with a touch of
                                  // tracking, which is what stops a seven-item bar reading as one word.
                                  fontFamily: CHROME_FONT, letterSpacing: "0.2px", lineHeight: 1.4,
                                  "&:hover": { backgroundColor: "#F1F3F4" } }}>
                    {menu.name}
                </Typography>
            ))}

            {/* disableRestoreFocus, AND IT IS NOT A DETAIL. A MUI Menu is a Modal, and a Modal
                puts the keyboard focus BACK on whatever opened it when it closes -- after the
                command has run. So every menu item that leaves the caret in the document --
                Insert ▸ Footer, Bold, a heading -- handed the keyboard to the menu bar instead,
                and the next thing typed went nowhere. The caret was in the right place and
                invisible, which is the worst version of this bug: the editor looked ready and
                swallowed everything.

                Found by probing document.activeElement after Insert ▸ Footer: ProseMirror's own
                selection was correct and sitting in the footer, and the focused element was a
                MuiTypography in the menu bar. */}
            <Menu anchorEl={open?.el} open={!!open} onClose={() => setOpen(null)}
                  disableRestoreFocus
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  slotProps={{ list: { dense: true,
                                       sx: { minWidth: 268, py: 0.5, fontFamily: CHROME_FONT } } }}>
                {items.map((item, index) => ([
                    item.divider && index > 0
                        ? <Divider key={`d${index}`} sx={{ my: 0.5 }}/> : null,
                    <Row key={item.label} item={item} onDone={() => setOpen(null)}/>,
                ]))}
            </Menu>

            <Box sx={{ flexGrow: 1 }}/>
            <Box sx={{ borderBottom: `1px solid ${Grey3}`, width: 0 }}/>
        </Box>
    );
}
